/**
 * Hook to connect to the per-user asset library YDoc room.
 *
 * Connects to /ws/yjs/_library/default via WebSocket and returns
 * library nodes as typed ReactFlow canvas nodes. Only connects when enabled=true.
 */

import { useEffect, useRef, useState } from 'react'
import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'

/** Access internal wsLastMessageReceived on WebsocketProvider (not in public types). */
function getWsLastMessageReceived(provider: WebsocketProvider): number | undefined {
  return provider.wsLastMessageReceived
}
function setWsLastMessageReceived(provider: WebsocketProvider, value: number): void {
  provider.wsLastMessageReceived = value
}
import { getConfiguredApiBase, getAuthToken } from '../api/client'
import { useAuthOptional } from '../contexts/authContextRaw'
import { readNodeDataFromYMap, type RawCanvasYNode, type ReactFlowCanvasNode } from './useYCanvas'
import { NODE_STATUSES, type NodeStatus } from '../generated/nodeDataTypes'

export interface UseLibraryYDocOptions {
  /** Only connect when true. Set to false to disconnect and save resources. */
  enabled?: boolean
}

export interface UseLibraryYDocResult {
  /** Library nodes as ReactFlow Node objects */
  nodes: ReactFlowCanvasNode[]
  /** Whether initial sync with server is complete */
  synced: boolean
  /** Whether WebSocket connection is live */
  connected: boolean
}

function yString(map: RawCanvasYNode, key: string, fallback: string): string {
  const value = map.get(key)
  return typeof value === 'string' ? value : fallback
}

function yNodeStatus(map: RawCanvasYNode, key: string, fallback: NodeStatus): NodeStatus {
  const value = yString(map, key, fallback)
  return NODE_STATUSES.find((candidate) => candidate === value) ?? fallback
}

function yNumber(map: RawCanvasYNode, key: string, fallback: number): number {
  const value = map.get(key)
  return typeof value === 'number' ? value : fallback
}

/**
 * Convert an HTTP(S) API base URL to a WebSocket URL.
 */
function toWsUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws')
}

/** Liveness watchdog matching useYDoc.ts. Two-phase timeout:
 *
 *  - INITIAL_HEARTBEAT_DEAD_MS (120s) applies while we're still waiting for
 *    the initial y-websocket sync handshake to complete. Fat-project /
 *    fat-library cold loads can legitimately take 15-20s wall-clock on
 *    the server (multi-MB YDoc read from Redis + pycrdt apply +
 *    migrators), AND on a freshly-booted instance under load they can
 *    queue behind in-flight cold loads. 120s is intentionally generous —
 *    killing the WS during this phase produced the "wave of reconnects"
 *    storm where each retry got killed in turn, each one consuming an
 *    SA pool connection on the server. Better to wait 2 min for a
 *    cold-load that's actually progressing than to thrash.
 *
 *  - HEARTBEAT_DEAD_MS (5s) applies AFTER the initial sync completes.
 *    Now y-websocket's resyncInterval=2s drives steady inbound traffic,
 *    so a 5s gap genuinely means the connection is silently dead and
 *    fast detection is appropriate.
 *
 *  The phase switch is driven by provider.synced (tracked via
 *  syncedRef): false during cold-load, flips to true on the first
 *  successful sync, resets to false on disconnect. */
const HEARTBEAT_INTERVAL_MS = 2000
const HEARTBEAT_DEAD_MS = 5000
const INITIAL_HEARTBEAT_DEAD_MS = 120000

function isLocalDebugUserMode(): boolean {
  return import.meta.env.DEV &&
    !import.meta.env.VITE_ENABLE_AUTH &&
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
}

export function readLibraryNodeFromYMap(
  ynode: RawCanvasYNode,
  nodeId: string,
  onInvalidNode?: (message: string) => void,
): ReactFlowCanvasNode | null {
  const nodeType = yString(ynode, 'type', '')
  let dataRaw: ReturnType<typeof readNodeDataFromYMap>
  try {
    dataRaw = readNodeDataFromYMap(ynode)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    onInvalidNode?.(`Skipping invalid library node ${nodeId} (${nodeType || '<missing>'}): ${message}`)
    return null
  }

  return {
    id: nodeId,
    type: nodeType,
    position: {
      x: yNumber(ynode, 'x', 0),
      y: yNumber(ynode, 'y', 0),
    },
    data: {
      ...dataRaw,
      status: yNodeStatus(ynode, 'status', 'idle'),
      label: yString(ynode, 'label', nodeId),
    },
  }
}

export function useLibraryYDoc({
  enabled = true,
}: UseLibraryYDocOptions = {}): UseLibraryYDocResult {
  // useAuthOptional rather than useAuth: ReferenceSelector is always
  // mounted inside CanvasToolbox, so this hook runs in test harnesses
  // that legitimately omit AuthProvider. Treat null context (no provider)
  // as unauthenticated.
  const auth = useAuthOptional()
  const isAuthenticated = auth?.isAuthenticated ?? false
  const [nodes, setNodes] = useState<ReactFlowCanvasNode[]>([])
  const [synced, setSynced] = useState(false)
  const [connected, setConnected] = useState(false)
  const ydocRef = useRef<Y.Doc | null>(null)
  const providerRef = useRef<WebsocketProvider | null>(null)

  // Auth gate: skip connection entirely when the user is signed out so we
  // don't loop reconnects with a missing/expired token. Local dev is the
  // exception: the backend accepts tokenless library WS connections as
  // debug-user, matching ProtectedRoute's local unauthenticated workspace path.
  const shouldConnect = enabled && (isAuthenticated || isLocalDebugUserMode())

  useEffect(() => {
    if (!shouldConnect) {
      // Clean up if disabled
      if (providerRef.current) {
        providerRef.current.destroy()
        providerRef.current = null
      }
      if (ydocRef.current) {
        ydocRef.current.destroy()
        ydocRef.current = null
      }
      setNodes([])
      setSynced(false)
      setConnected(false)
      return
    }

    const ydoc = new Y.Doc()
    ydocRef.current = ydoc
    let cancelled = false
    let watchdogTimer: ReturnType<typeof setInterval> | null = null

    const stopWatchdog = () => {
      if (watchdogTimer != null) {
        clearInterval(watchdogTimer)
        watchdogTimer = null
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && providerRef.current) {
        setWsLastMessageReceived(providerRef.current, Date.now())
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    const connect = async () => {
      const token = await getAuthToken()
      if (cancelled) return

      const apiBase = getConfiguredApiBase()
      const wsBase = toWsUrl(apiBase)
      // Library endpoint: /ws/yjs/_library with room name "default"
      // The server constructs the full room name library:{user_id}/default from the JWT
      const wsUrl = `${wsBase}/ws/yjs/_library`
      const roomName = 'default'

      const params: Record<string, string> = {}
      if (token) {
        params['token'] = token
      }

      const provider = new WebsocketProvider(wsUrl, roomName, ydoc, {
        connect: true,
        params,
        // Sync messages drive inbound traffic for the watchdog (every 2s
        // so a 5s dead-timeout has enough samples to detect silent
        // disconnects within ~5s).
        resyncInterval: 2000,
        // Cap above the y-websocket default of 2.5s so transient blips
        // don't hammer the server with reconnect attempts.
        maxBackoffTime: 5000,
      })

      provider.on('status', async ({ status }: { status: string }) => {
        if (cancelled) return
        setConnected(status === 'connected')
        if (status === 'disconnected') {
          // Refresh the JWT before y-websocket's auto-reconnect fires —
          // otherwise we keep retrying with a token that's already expired.
          // Mirrors the handler in useYDoc.ts.
          try {
            const freshToken = await getAuthToken()
            if (freshToken && provider.params) {
              provider.params['token'] = freshToken
            }
          } catch {
            // Swallow — y-websocket will retry on the next cycle.
          }
        }
      })

      // Tracks whether the y-websocket sync handshake has completed on the
      // current connection. Used by the watchdog to pick between the longer
      // INITIAL_HEARTBEAT_DEAD_MS (pre-sync, tolerates cold-load) and the
      // shorter HEARTBEAT_DEAD_MS (post-sync, fast disconnect detection).
      // y-websocket emits sync=false on disconnect, so this resets across
      // reconnects automatically.
      const syncedRef = { current: false }
      provider.on('sync', (isSynced: boolean) => {
        syncedRef.current = isSynced
        setSynced(isSynced)
      })

      providerRef.current = provider

      // Liveness watchdog: force-close + reconnect if no inbound for
      // longer than the phase-appropriate threshold (see comments on
      // HEARTBEAT_DEAD_MS / INITIAL_HEARTBEAT_DEAD_MS above). y-websocket's
      // onclose then schedules its automatic reconnect.
      stopWatchdog()
      watchdogTimer = setInterval(() => {
        if (cancelled || !provider) return
        const lastMs = getWsLastMessageReceived(provider)
        if (!lastMs) return
        const deadMs = syncedRef.current ? HEARTBEAT_DEAD_MS : INITIAL_HEARTBEAT_DEAD_MS
        if (Date.now() - lastMs > deadMs) {
          if (provider.ws) {
            try { provider.ws.close() } catch { /* already closed */ }
          }
        }
      }, HEARTBEAT_INTERVAL_MS)

      // Observe the 'nodes' map for changes and rebuild ReactFlow nodes
      const yNodes = ydoc.getMap<RawCanvasYNode>('nodes')
      const warnedInvalidNodeIds = new Set<string>()

      const rebuildNodes = () => {
        const result: ReactFlowCanvasNode[] = []
        yNodes.forEach((ynode: RawCanvasYNode, nodeId: string) => {
          const node = readLibraryNodeFromYMap(ynode, nodeId, (message) => {
            if (warnedInvalidNodeIds.has(nodeId)) return
            warnedInvalidNodeIds.add(nodeId)
            console.warn(`[library-ydoc] ${message}`)
          })
          if (!node) return
          result.push(node)
        })
        setNodes(result)
      }

      yNodes.observeDeep(rebuildNodes)
      // Initial build once connected
      rebuildNodes()
    }

    connect()

    return () => {
      cancelled = true
      stopWatchdog()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      if (providerRef.current) {
        providerRef.current.destroy()
        providerRef.current = null
      }
      if (ydocRef.current) {
        ydocRef.current.destroy()
        ydocRef.current = null
      }
      setConnected(false)
      setSynced(false)
      setNodes([])
    }
  }, [shouldConnect])

  return { nodes, synced, connected }
}
